import { useEffect, useState, useRef } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";

// Pages
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import RegisterPage from "./pages/RegisterPage";
import RegisterCompanyPage from "./pages/RegisterCompanyPage";
import CatalogPage from "./pages/CatalogPage";
import VehicleDetailPage from "./pages/VehicleDetailPage";

// Protected Pages
import CustomerDashboard from "./pages/customer/CustomerDashboard";
import CustomerBookings from "./pages/customer/CustomerBookings";
import CustomerProfile from "./pages/customer/CustomerProfile";

import CompanyDashboard from "./pages/company/CompanyDashboard";
import CompanyVehicles from "./pages/company/CompanyVehicles";
import CompanyBookings from "./pages/company/CompanyBookings";
import CompanyProfile from "./pages/company/CompanyProfile";
import CompanySubscription from "./pages/company/CompanySubscription";
import CompanyCheckout from "./pages/company/CompanyCheckout";
import CompanySupport from "./pages/company/CompanySupport";
import CompanyPricing from "./pages/company/CompanyPricing";

import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminCompanies from "./pages/admin/AdminCompanies";
import AdminVehicles from "./pages/admin/AdminVehicles";
import AdminBookings from "./pages/admin/AdminBookings";
import AdminSubscriptions from "./pages/admin/AdminSubscriptions";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminTransactions from "./pages/admin/AdminTransactions";
import AdminCoupons from "./pages/admin/AdminCoupons";
import AdminEmails from "./pages/admin/AdminEmails";
import AdminTickets from "./pages/admin/AdminTickets";
import AdminAgents from "./pages/admin/AdminAgents";
import AdminSiteSettings from "./pages/admin/AdminSiteSettings";
import AdminVehicleCategories from "./pages/admin/AdminVehicleCategories";

// Context
import { AuthProvider, useAuth } from "./context/AuthContext";
import { SiteSettingsProvider } from "./context/SiteSettingsContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function AuthCallback() {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash;
    const sessionIdMatch = hash.match(/session_id=([^&]+)/);
    
    if (sessionIdMatch) {
      const sessionId = sessionIdMatch[1];
      
      axios.post(`${API}/auth/session`, { session_id: sessionId }, { withCredentials: true })
        .then(response => {
          const user = response.data;
          localStorage.setItem("user", JSON.stringify(user));
          localStorage.setItem("isAuthenticated", "true");
          
          // Redirect based on role
          if (user.role === "admin") {
            navigate("/admin", { state: { user }, replace: true });
          } else if (user.role === "company") {
            navigate("/company", { state: { user }, replace: true });
          } else {
            navigate("/dashboard", { state: { user }, replace: true });
          }
        })
        .catch(error => {
          console.error("Auth error:", error);
          navigate("/login", { replace: true });
        });
    } else {
      navigate("/login", { replace: true });
    }
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-muted-foreground">Autenticazione in corso...</p>
      </div>
    </div>
  );
}

function ProtectedRoute({ children, allowedRoles }) {
  const { user, isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    // Redirect to appropriate dashboard based on role
    if (user?.role === "admin") {
      return <Navigate to="/admin" replace />;
    } else if (user?.role === "company") {
      return <Navigate to="/company" replace />;
    } else {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return children;
}

function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/registra-azienda" element={<RegisterCompanyPage />} />
      <Route path="/catalogo" element={<CatalogPage />} />
      <Route path="/veicolo/:vehicleId" element={<VehicleDetailPage />} />

      {/* Customer Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute allowedRoles={["customer", "admin"]}>
          <CustomerDashboard />
        </ProtectedRoute>
      } />
      <Route path="/prenotazioni" element={
        <ProtectedRoute allowedRoles={["customer", "admin"]}>
          <CustomerBookings />
        </ProtectedRoute>
      } />
      <Route path="/profilo" element={
        <ProtectedRoute>
          <CustomerProfile />
        </ProtectedRoute>
      } />

      {/* Company Routes */}
      <Route path="/company" element={
        <ProtectedRoute allowedRoles={["company", "admin"]}>
          <CompanyDashboard />
        </ProtectedRoute>
      } />
      <Route path="/company/veicoli" element={
        <ProtectedRoute allowedRoles={["company", "admin"]}>
          <CompanyVehicles />
        </ProtectedRoute>
      } />
      <Route path="/company/prenotazioni" element={
        <ProtectedRoute allowedRoles={["company", "admin"]}>
          <CompanyBookings />
        </ProtectedRoute>
      } />
      <Route path="/company/tariffe" element={
        <ProtectedRoute allowedRoles={["company", "admin"]}>
          <CompanyPricing />
        </ProtectedRoute>
      } />
      <Route path="/company/profilo" element={
        <ProtectedRoute allowedRoles={["company", "admin"]}>
          <CompanyProfile />
        </ProtectedRoute>
      } />
      <Route path="/company/abbonamento" element={
        <ProtectedRoute allowedRoles={["company", "admin"]}>
          <CompanySubscription />
        </ProtectedRoute>
      } />
      <Route path="/company/checkout" element={
        <ProtectedRoute allowedRoles={["company", "admin"]}>
          <CompanyCheckout />
        </ProtectedRoute>
      } />
      <Route path="/company/supporto" element={
        <ProtectedRoute allowedRoles={["company"]}>
          <CompanySupport />
        </ProtectedRoute>
      } />
      <Route path="/company/supporto/:ticketId" element={
        <ProtectedRoute allowedRoles={["company"]}>
          <CompanySupport />
        </ProtectedRoute>
      } />

      {/* Admin Routes */}
      <Route path="/admin" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminDashboard />
        </ProtectedRoute>
      } />
      <Route path="/admin/utenti" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminUsers />
        </ProtectedRoute>
      } />
      <Route path="/admin/aziende" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminCompanies />
        </ProtectedRoute>
      } />
      <Route path="/admin/veicoli" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminVehicles />
        </ProtectedRoute>
      } />
      <Route path="/admin/prenotazioni" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminBookings />
        </ProtectedRoute>
      } />
      <Route path="/admin/abbonamenti" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminSubscriptions />
        </ProtectedRoute>
      } />
      <Route path="/admin/impostazioni" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminSettings />
        </ProtectedRoute>
      } />
      <Route path="/admin/transazioni" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminTransactions />
        </ProtectedRoute>
      } />
      <Route path="/admin/sconti" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminCoupons />
        </ProtectedRoute>
      } />
      <Route path="/admin/email" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminEmails />
        </ProtectedRoute>
      } />
      <Route path="/admin/ticket" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminTickets />
        </ProtectedRoute>
      } />
      <Route path="/admin/ticket/:ticketId" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminTickets />
        </ProtectedRoute>
      } />
      <Route path="/admin/agenti" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminAgents />
        </ProtectedRoute>
      } />
      <Route path="/admin/sito" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminSiteSettings />
        </ProtectedRoute>
      } />
      <Route path="/admin/categorie-veicoli" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminVehicleCategories />
        </ProtectedRoute>
      } />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <SiteSettingsProvider>
          <AppRouter />
          <Toaster position="top-right" />
        </SiteSettingsProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
